import Card from "@/components/Card";
import externalLinks from "@/constants/externalLinks";
import { FormField } from "@/ui-lib/components/Form";
import ParsedText from "@/ui-lib/components/ParsedText";
import TextInput from "@/ui-lib/components/TextInput";
import { AlertType, postAlert } from "@/utils/alerts";
import { useTheme } from "@emotion/react";
import { faCopy, faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import {
  ReactFormApi,
  ReactFormExtendedApi,
  useStore,
} from "@tanstack/react-form";
import Button from "@ternary/api-lib/ui-lib/components/Button";
import Flex from "@ternary/api-lib/ui-lib/components/Flex";
import Icon from "@ternary/api-lib/ui-lib/components/Icon";
import Link from "@ternary/api-lib/ui-lib/components/Link";
import Text from "@ternary/api-lib/ui-lib/components/Text";
import Divider from "@ternary/web-ui-lib/components/Divider";
import React from "react";
import copyText from "../../copyText";
import { AWS_ROLE_ARN_REGEX } from "../AwsIntegrationForm/constants";
import {
  FullyQualifiedResourceType,
  parseFullyQualifiedResourceString,
} from "../FullyQualifiedResourceString";
import { focusBillingFormSchema, FormData } from "./types";

const GCS_PERMISSIONS = [
  "resourcemanager.projects.get",
  "resourcemanager.projects.list",
  "storage.folders.get",
  "storage.folders.list",
  "storage.managedFolders.get",
  "storage.managedFolders.list",
  "storage.objects.get",
  "storage.objects.list",
];

const S3_PERMISSIONS = [
  "s3:ListBucketVersions",
  "s3:ListBucket",
  "s3:GetObjectAcl",
  "s3:GetObject",
  "s3:GetBucketLocation",
  "s3:GetBucketAcl",
];

const validateField = (fieldName: keyof FormData, value: string) => {
  const result = focusBillingFormSchema.shape[fieldName].safeParse(value);

  return !result.success ? copyText.errorInputFieldRequired : undefined;
};

const createFieldValidators = (fieldName: keyof FormData) => ({
  onChange: ({ value }: { value: string }) => validateField(fieldName, value),
  onMount: ({ value }: { value: string }) => validateField(fieldName, value),
});

const validateRoleArn = (
  value: string | null | undefined,
  type?: FullyQualifiedResourceType
) => {
  if (type === "S3Ref" && !value) {
    return copyText.errorInputFieldRequired;
  }

  if (value && !AWS_ROLE_ARN_REGEX.test(value)) {
    return copyText.errorInputFieldArnError;
  }

  return undefined;
};

interface Props {
  form: ReactFormExtendedApi<FormData, undefined> &
    ReactFormApi<FormData, undefined>;
  referenceType?: FullyQualifiedResourceType;
  setReferenceType: (type: FullyQualifiedResourceType | undefined) => void;
}

export function GenericFocusIntegrationFormGeneral({
  form,
  referenceType,
  setReferenceType,
}: Props) {
  const theme = useTheme();
  const storageUri = useStore(form.store, (state) => state.values.storageUri);

  try {
    const { type } = parseFullyQualifiedResourceString(storageUri);

    if (type !== referenceType) {
      setReferenceType(type);
    }
  } catch {
    // Do nothing. Reference type already initialized.
  }

  const handleStorageUriChange = (value: string, shouldReset = false) => {
    try {
      const { type } = parseFullyQualifiedResourceString(value);
      if (type !== referenceType) {
        setReferenceType(type);
      }
    } catch {
      if (shouldReset) {
        setReferenceType(undefined);
      }
    }

    form.setFieldValue("storageUri", value);
    form.validateField("storageUri", "change");
  };

  const handleCopyPermissions = () => {
    const permissions =
      referenceType === "GCSBucket" ? GCS_PERMISSIONS : S3_PERMISSIONS;
    navigator.clipboard.writeText(permissions.join("\n"));
    postAlert({
      message: copyText.copiedToClipboardText,
      type: AlertType.SUCCESS,
    });
  };

  return (
    <Flex direction="column" gap={theme.space_xs}>
      <Text>{copyText.genericFocusFormDescription}</Text>
      <form.Field name="name" validators={createFieldValidators("name")}>
        {(field) => (
          <FormField
            label={copyText.nameInputLabel}
            required
            input={TextInput}
            onChange={(e) => field.handleChange(e.target.value)}
            value={field.state.value}
            errorCaption={
              field.state.meta.isTouched
                ? field.state.meta.errors.join(", ")
                : ""
            }
          />
        )}
      </form.Field>
      <form.Field
        name="storageUri"
        validators={createFieldValidators("storageUri")}
      >
        {(field) => (
          <FormField
            label={copyText.genericFocusFormStorageUriInputLabel}
            caption={copyText.genericFocusFormStorageUriInputDescription}
            required
            input={TextInput}
            value={field.state.value}
            onChange={(e) => handleStorageUriChange(e.target.value)}
            onBlur={(e) =>
              handleStorageUriChange(e.target.value, true /* shouldReset */)
            }
          />
        )}
      </form.Field>
      {referenceType === "S3Ref" && (
        <form.Field
          name="roleArn"
          validators={{
            onChange: ({ value }) => validateRoleArn(value, referenceType),
            onMount: ({ value }) => validateRoleArn(value, referenceType),
          }}
        >
          {(field) => (
            <FormField
              label={copyText.genericFocusFormRoleArnInputLabel}
              caption={copyText.genericFocusFormRoleArnInputDescription}
              required
              input={TextInput}
              value={field.state.value || ""}
              onChange={(e) => field.handleChange(e.target.value)}
              errorCaption={
                field.state.meta.isTouched
                  ? field.state.meta.errors.join(", ")
                  : ""
              }
            />
          )}
        </form.Field>
      )}
      {referenceType && (
        <Card>
          <Flex alignItems="center" gap={theme.space_xs}>
            <Icon icon={faInfoCircle} />
            <Text>
              <ParsedText
                text={copyText.genericFocusFormBucketPermissionsHeading}
                components={{
                  provider: referenceType === "GCSBucket" ? "GCS" : "S3",
                }}
              />
            </Text>
          </Flex>
          {referenceType === "S3Ref" && (
            <Text>
              <ParsedText
                text={copyText.genericFocusFormBucketPermissionsNoteS3}
                components={{
                  link: (
                    <Link
                      to={`${externalLinks.readmeAwsOnboardingBucketPermissionsDocumentation}`}
                      rel="noreferrer"
                      target="_blank"
                    />
                  ),
                }}
              />
            </Text>
          )}
          <Divider margin={theme.space_sm} />
          <Flex
            direction="column"
            gap={theme.space_xxs}
            style={{ position: "relative" }}
          >
            <Button
              secondary
              type="button"
              size="tiny"
              style={{ position: "absolute", right: 4, top: 4 }}
              iconStart={<Icon icon={faCopy} />}
              onClick={handleCopyPermissions}
            />
            {(referenceType === "GCSBucket"
              ? GCS_PERMISSIONS
              : S3_PERMISSIONS
            ).map((permission) => (
              <Text key={permission} style={{ fontFamily: "monospace" }}>
                {permission}
              </Text>
            ))}
          </Flex>
        </Card>
      )}
    </Flex>
  );
}
